import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, X, BarChart3, ThumbsUp, ThumbsDown, Activity,
  TrendingUp, Zap, List,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { value: 7,  label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

// ── Stat card ────────────────────────────────────────────────────────────────
const Stat = ({ icon: Icon, label, value, accent = 'text-white' }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-white/40 text-xs mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <p className={`text-xl font-bold ${accent}`}>{value}</p>
  </div>
);

// ── Spark bar chart ──────────────────────────────────────────────────────────
const DailyFiresChart = ({ daily }) => {
  const max = Math.max(...daily.map(d => d.count), 1);
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-white/60 text-xs font-semibold mb-3 flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5 text-teal-400" />
        Fires per day
      </p>
      <div className="flex items-end gap-0.5 h-28">
        {daily.map(d => {
          const h = d.count > 0 ? Math.max(8, Math.round((d.count / max) * 100)) : 2;
          return (
            <div
              key={d.date}
              className="flex-1 bg-teal-500/40 hover:bg-teal-400 transition-colors rounded-t"
              style={{ height: `${h}%`, opacity: d.count === 0 ? 0.15 : 1 }}
              title={`${d.date}: ${d.count} fires`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-white/20 font-mono">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
};

// ── Confidence buckets ───────────────────────────────────────────────────────
const ConfidenceBars = ({ buckets, total }) => {
  const order = ['0.9+', '0.75-0.9', '0.5-0.75', '<0.5'];
  const colors = {
    '0.9+':     'bg-emerald-400',
    '0.75-0.9': 'bg-teal-400',
    '0.5-0.75': 'bg-amber-400',
    '<0.5':     'bg-red-400',
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-white/60 text-xs font-semibold mb-3">Confidence distribution</p>
      <div className="space-y-2">
        {order.map(k => {
          const n = buckets[k] || 0;
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          return (
            <div key={k}>
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-white/50 font-mono">{k}</span>
                <span className="text-white/30">{n} ({pct}%)</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className={`${colors[k]} h-full transition-all`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Top extracted values ─────────────────────────────────────────────────────
const TopExtracted = ({ topExtracted }) => {
  const entries = Object.entries(topExtracted || {});
  if (!entries.length) return null;
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-white/60 text-xs font-semibold mb-3 flex items-center gap-1.5">
        <List className="w-3.5 h-3.5 text-violet-400" />
        Top extracted values
      </p>
      <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
        {entries.map(([field, vals]) => (
          <div key={field}>
            <p className="text-teal-300 text-xs font-mono mb-1">{field}</p>
            <div className="space-y-1">
              {vals.map(v => (
                <div key={v.value} className="flex items-center justify-between bg-white/5 rounded px-2 py-1 text-xs">
                  <span className="text-white/70 truncate flex-1 mr-2" title={v.value}>{v.value}</span>
                  <span className="text-white/40 font-mono shrink-0">{v.count}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main modal ───────────────────────────────────────────────────────────────
export const RuleMetricsModal = ({ rule, domainId, token, onClose }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(
      `${API}/admin/domains/${domainId}/voicefabric/rules/${rule.id}/metrics?days=${range}`,
      { headers }
    )
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load metrics'))
      .finally(() => setLoading(false));
  }, [rule.id, domainId, range, headers]);

  const thumbsRate = (data && data.total_fires > 0)
    ? Math.round((data.feedback.positive / data.total_fires) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-white font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-teal-400" />
              Metrics: {rule.name}
            </h2>
            <p className="text-white/40 text-xs mt-0.5">
              Rule performance, feedback and extracted-value trends
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
              {RANGES.map(r => (
                <button
                  key={r.value}
                  onClick={() => setRange(r.value)}
                  data-testid={`metrics-range-${r.value}`}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    range === r.value ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-6">
          {loading || !data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-7 h-7 text-white/20 animate-spin" />
            </div>
          ) : data.total_fires === 0 ? (
            <div className="text-center py-16">
              <Zap className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <p className="text-white/50 text-sm mb-1">No fires in the last {range} days</p>
              <p className="text-white/30 text-xs">Metrics will populate as calls match this rule.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={Zap} label={`Fires (${range}d)`} value={data.total_fires} accent="text-teal-400" />
                <Stat icon={Activity} label="Avg confidence" value={`${Math.round(data.avg_confidence * 100)}%`} />
                <Stat icon={ThumbsUp} label="👍 Thumbs up" value={data.feedback.positive} accent="text-emerald-400" />
                <Stat icon={ThumbsDown} label="👎 Thumbs down" value={data.feedback.negative} accent="text-red-400" />
              </div>

              <DailyFiresChart daily={data.daily_fires} />

              <div className="grid md:grid-cols-2 gap-4">
                <ConfidenceBars buckets={data.confidence_buckets} total={data.total_fires} />
                <TopExtracted topExtracted={data.top_extracted} />
              </div>

              {data.feedback.positive + data.feedback.negative > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 text-xs">
                  <p className="text-emerald-300 mb-1 font-semibold">Rule precision (based on admin feedback)</p>
                  <p className="text-white/60">
                    {thumbsRate}% of reviewed actions were approved ({data.feedback.positive} 👍 /
                    {data.feedback.negative} 👎, {data.feedback.neutral} unreviewed).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
